import React, { useState, useEffect, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBookmark,
  faStickyNote,
  faClockRotateLeft,
  faClock,
  faExternalLinkAlt,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../../contexts/UserContext";
import apiService from "../../../ApiService";
import MarkdownRenderer from "./MarkdownRenderer";
import "./SubjectDetail.css";

const SubjectDetail = ({ subjectId, onBack }) => {
  const { user } = useContext(UserContext);
  const userId = user?.userId;
  const [subject, setSubject] = useState(null);
  const [sources, setSources] = useState([]);
  const [notes, setNotes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("sources");

  useEffect(() => {
    fetchSubjectDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  const fetchSubjectDetails = async () => {
    setLoading(true);
    try {
      // Fetch subject details
      const subjectData = await apiService.getSubjectById(userId, subjectId);
      setSubject(subjectData);

      // Fetch sources for this subject
      const sourcesData = await apiService.getSources(userId, {
        subject_id: subjectId,
      });
      setSources(sourcesData.sources || []);

      // Fetch notes for this subject
      const notesData = await apiService.getNotes(userId, {
        subject_id: subjectId,
      });
      setNotes(notesData.notes || []);

      // Fetch study logs for this subject
      const logsData = await apiService.getStudyLogs(userId, {
        subject_id: subjectId,
      });
      setLogs(logsData.study_logs || []);
    } catch (error) {
      console.error("Error fetching subject details:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not available";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Invalid date";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getTotalStudyTime = () => {
    return logs.reduce((total, log) => total + (log.duration_minutes || 0), 0);
  };

  if (loading) {
    return (
      <div className="subject-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading subject details...</p>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="subject-detail-error">
        <p>Subject not found</p>
        <button onClick={onBack} className="back-btn">
          <FontAwesomeIcon icon={faArrowLeft} /> Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="subject-detail">
      {/* Header */}
      <div className="detail-header">
        <button onClick={onBack} className="back-btn">
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        <div className="subject-title-section">
          <h1>{subject.subject_name}</h1>
          {subject.description && (
            <MarkdownRenderer
              content={subject.description}
              className="subject-description"
            />
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="detail-stats">
        <div className="stat-item">
          <FontAwesomeIcon icon={faBookmark} />
          <div>
            <h3>{sources.length}</h3>
            <p>Sources</p>
          </div>
        </div>
        <div className="stat-item">
          <FontAwesomeIcon icon={faStickyNote} />
          <div>
            <h3>{notes.length}</h3>
            <p>Notes</p>
          </div>
        </div>
        <div className="stat-item">
          <FontAwesomeIcon icon={faClockRotateLeft} />
          <div>
            <h3>{logs.length}</h3>
            <p>Study Sessions</p>
          </div>
        </div>
        <div className="stat-item">
          <FontAwesomeIcon icon={faClock} />
          <div>
            <h3>{formatDuration(getTotalStudyTime())}</h3>
            <p>Total Time</p>
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="detail-tabs">
        <button
          className={`detail-tab ${activeSection === "sources" ? "active" : ""}`}
          onClick={() => setActiveSection("sources")}
        >
          <FontAwesomeIcon icon={faBookmark} /> Sources ({sources.length})
        </button>
        <button
          className={`detail-tab ${activeSection === "notes" ? "active" : ""}`}
          onClick={() => setActiveSection("notes")}
        >
          <FontAwesomeIcon icon={faStickyNote} /> Notes ({notes.length})
        </button>
        <button
          className={`detail-tab ${activeSection === "logs" ? "active" : ""}`}
          onClick={() => setActiveSection("logs")}
        >
          <FontAwesomeIcon icon={faClockRotateLeft} /> Study Logs ({logs.length}
          )
        </button>
      </div>

      {/* Content Area */}
      <div className="detail-content">
        {/* Sources */}
        {activeSection === "sources" && (
          <div className="sources-section">
            {sources.length === 0 ? (
              <div className="empty-section">
                <FontAwesomeIcon icon={faBookmark} />
                <p>No sources added yet</p>
              </div>
            ) : (
              <div className="sources-list">
                {sources.map((source) => (
                  <div key={source.source_id} className="source-item">
                    <div className="source-header">
                      <h3>{source.source_title || source.source_name}</h3>
                      <span className="source-type">{source.source_type}</span>
                    </div>
                    {source.source_url && (
                      <a
                        href={source.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="source-link"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} /> Open Source
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {activeSection === "notes" && (
          <div className="notes-section">
            {notes.length === 0 ? (
              <div className="empty-section">
                <FontAwesomeIcon icon={faStickyNote} />
                <p>No notes created yet</p>
              </div>
            ) : (
              <div className="notes-list">
                {notes.map((note) => (
                  <div key={note.note_id} className="note-item">
                    <h3>{note.title}</h3>
                    {note.source_name && (
                      <p className="note-source">
                        <FontAwesomeIcon icon={faBookmark} /> {note.source_name}
                      </p>
                    )}
                    {note.content && (
                      <MarkdownRenderer
                        content={note.content}
                        className="note-content"
                      />
                    )}
                    {note.tags && (
                      <div className="note-tags">
                        <FontAwesomeIcon icon={faTags} />
                        {note.tags.split(",").map((tag, idx) => (
                          <span key={idx} className="tag">
                            #{tag.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="note-date">{formatDate(note.created_date)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Study Logs */}
        {activeSection === "logs" && (
          <div className="logs-section">
            {logs.length === 0 ? (
              <div className="empty-section">
                <FontAwesomeIcon icon={faClockRotateLeft} />
                <p>No study sessions logged yet</p>
              </div>
            ) : (
              <div className="logs-list">
                {logs.map((log) => (
                  <div key={log.log_id} className="log-item">
                    <div className="log-header">
                      <span className="log-date">
                        {formatDate(log.study_date)}
                      </span>
                      <span className="log-duration">
                        <FontAwesomeIcon icon={faClock} />{" "}
                        {formatDuration(log.duration_minutes)}
                      </span>
                    </div>
                    {log.source_name && (
                      <p className="log-source">
                        <FontAwesomeIcon icon={faBookmark} /> {log.source_name}
                      </p>
                    )}
                    {log.notes && (
                      <MarkdownRenderer
                        content={log.notes}
                        className="log-notes"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectDetail;
